import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Box } from '@lifeomic/chroma-react/components/Box';
import { Text } from '@lifeomic/chroma-react/components/Text';
import { DotLoader } from '@lifeomic/chroma-react/components/DotLoader';
import { makeStyles } from '@lifeomic/chroma-react/styles';
import { defineMessages, useIntl } from 'react-intl';

import { client, useDataset } from '../../../util/api';
import { getDocumentReferencePatientId, getFileId } from './fhir';
import type { InvalidFileDocumentReference } from './types';

const messages = defineMessages({
  previewTitle: {
    id: 'FileValidationResolver.previewTitle',
    defaultMessage: 'Document preview',
  },
  previewLoading: {
    id: 'FileValidationResolver.previewLoading',
    defaultMessage: 'Loading document preview…',
  },
  previewUnavailable: {
    id: 'FileValidationResolver.previewUnavailable',
    defaultMessage: 'Document preview is unavailable for this file.',
  },
  previewError: {
    id: 'FileValidationResolver.previewError',
    defaultMessage: 'Unable to load the document preview.',
  },
});

const useStyles = makeStyles(theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
    height: '100%',
    minHeight: 0,
    width: '100%',
  },
  frame: {
    flex: 1,
    display: 'block',
    width: '100%',
    height: '100%',
    minHeight: theme.pxToRem(520),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
  },
  fallback: {
    border: `1px dashed ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    minHeight: theme.pxToRem(520),
    width: '100%',
  },
}));

const PreviewFrame = React.memo(({ downloadUrl }: { downloadUrl: string }) => {
  const classes = useStyles();

  return (
    <iframe
      className={classes.frame}
      title="file-validation-document-preview"
      src={`${downloadUrl}#zoom=100#view=Fit#toolbar=0`}
    />
  );
});

PreviewFrame.displayName = 'PreviewFrame';

export const DocumentPreview: React.FC<{
  documentReference: InvalidFileDocumentReference;
}> = ({ documentReference }) => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const dataset = useDataset();
  const fileId = getFileId(documentReference);
  const subject = getDocumentReferencePatientId(documentReference);

  const query = useQuery({
    queryKey: ['file-validation-preview', dataset.id, fileId, subject],
    enabled: Boolean(fileId && subject),
    cacheTime: 0,
    queryFn: async () => {
      const response = await client.get<{ downloadUrl?: string }>(
        `/v1/fhir/files/projects/${encodeURIComponent(
          dataset.id,
        )}/files/${encodeURIComponent(fileId || '')}`,
        {
          params: {
            subject,
            include: 'downloadUrl',
            includeContentDisposition: false,
          },
        },
      );

      return response.data;
    },
  });

  const fallback = (message: string, isLoading = false) => (
    <Box
      className={classes.fallback}
      align="center"
      justify="center"
      fullWidth
      padding={3}
      gap={1}
    >
      {isLoading ? <DotLoader /> : null}
      <Text size="subbody">{message}</Text>
    </Box>
  );

  return (
    <section
      className={classes.root}
      aria-label={formatMessage(messages.previewTitle)}
    >
      <Text size="headline">{formatMessage(messages.previewTitle)}</Text>
      {!fileId || !subject ? (
        fallback(formatMessage(messages.previewUnavailable))
      ) : query.isLoading ? (
        fallback(formatMessage(messages.previewLoading), true)
      ) : query.isError ? (
        fallback(formatMessage(messages.previewError))
      ) : query.data?.downloadUrl ? (
        <PreviewFrame downloadUrl={query.data.downloadUrl} />
      ) : (
        fallback(formatMessage(messages.previewUnavailable))
      )}
    </section>
  );
};
